'use client';

import { useState } from 'react';
import type { TFunction } from 'i18next';
import { ChevronDown, ChevronRight, Gavel, LucideIcon } from 'lucide-react';
import type { TenderItem } from '@/lib/backend-api';
import { tryBuildCompanySlugFromCanonical } from '@/lib/company-utils';
import type { Locale } from '@/lib/i18n-routes';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { LocaleLink } from '@/components/locale-link';
import { formatCurrency, formatDateISO, type BoardEvent, type CapitalEvent, type CompanyEvent, type InsolvencyEvent } from './companyHistoryEvents';

// =============================================================================
// TABLE RENDERERS
// =============================================================================

const headerCls =
  'relative text-left align-middle text-xs font-normal text-accent-foreground px-2.5 h-8 border-b border-border first:pl-5 last:pr-5';
const cellCls = 'text-xs py-1.5 px-2 align-top first:pl-5 last:pr-5';

function EventIcon({
  icon: Icon,
  iconBg,
  iconColor,
}: {
  icon: LucideIcon;
  iconBg: string;
  iconColor: string;
}) {
  return (
    <div
      className={cn(
        'flex h-5 w-5 shrink-0 items-center justify-center rounded-md',
        iconBg,
      )}
    >
      <Icon size={11} className={iconColor} />
    </div>
  );
}

export function CompanyEventsTable({
  events,
  t,
  blurAfter,
}: {
  events: CompanyEvent[];
  t: TFunction;
  blurAfter?: number;
}) {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  if (events.length === 0) return null;
  return (
    <div className="max-h-[400px] overflow-auto">
      <table className="w-full table-fixed">
        <colgroup>
          <col className="w-[85px]" />
          <col />
          <col />
        </colgroup>
        <thead className="sticky top-0 z-10 bg-muted">
          <tr>
            <th className={cn(headerCls, 'whitespace-nowrap')}>
              {t('companyHistory.colDate')}
            </th>
            <th className={headerCls}>{t('companyHistory.colEventType')}</th>
            <th className={headerCls}>{t('companyHistory.colEvent')}</th>
          </tr>
        </thead>
        <tbody>
          {events.map((evt, idx) => {
            const isExpanded = evt.alwaysExpanded || expandedIdx === idx;
            const isCollapsible = !!evt.detail && !evt.alwaysExpanded;
            const isBlurred = blurAfter != null && idx >= blurAfter;
            return (
              <tr
                key={idx}
                className={cn(
                  'border-b border-border/40 transition-colors',
                  isCollapsible && !isBlurred && 'cursor-pointer',
                  isBlurred && 'select-none',
                  isExpanded && !evt.alwaysExpanded
                    ? 'bg-muted/50'
                    : 'hover:bg-muted/30',
                )}
                onClick={() =>
                  !isBlurred &&
                  isCollapsible &&
                  setExpandedIdx(isExpanded ? null : idx)
                }
              >
                <td
                  className={cn(
                    cellCls,
                    'text-muted-foreground whitespace-nowrap tabular-nums',
                    isBlurred && 'blur-[5px]',
                  )}
                >
                  {evt.date}
                </td>
                <td
                  className={cn(
                    cellCls,
                    'font-medium text-foreground',
                    isBlurred && 'blur-[5px]',
                  )}
                >
                  <div className="flex items-start gap-1.5">
                    <EventIcon
                      icon={evt.icon}
                      iconBg={evt.iconBg}
                      iconColor={evt.iconColor}
                    />
                    <span className="break-words min-w-0">{evt.title}</span>
                    {isCollapsible &&
                      !isBlurred &&
                      (isExpanded ? (
                        <ChevronDown
                          size={11}
                          className="text-muted-foreground/40 shrink-0 mt-0.5"
                        />
                      ) : (
                        <ChevronRight
                          size={11}
                          className="text-muted-foreground/40 shrink-0 mt-0.5"
                        />
                      ))}
                  </div>
                </td>
                <td className={cn(cellCls, isBlurred && 'blur-[5px]')}>
                  {evt.value && (
                    <span className="text-foreground break-words">
                      {evt.value}
                    </span>
                  )}
                  {evt.detail && (
                    <p
                      className={cn(
                        evt.value
                          ? 'text-muted-foreground/80 mt-0.5'
                          : 'text-foreground',
                        isExpanded
                          ? 'whitespace-pre-wrap break-words'
                          : 'line-clamp-2 break-words',
                      )}
                    >
                      {evt.detail}
                    </p>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function CapitalEventsTable({
  events,
  t,
  blurAfter,
}: {
  events: CapitalEvent[];
  t: TFunction;
  blurAfter?: number;
}) {
  if (events.length === 0) return null;
  return (
    <div className="max-h-[400px] overflow-auto">
      <table className="w-full table-fixed">
        <colgroup>
          <col className="w-[85px]" />
          <col />
          <col className="w-[90px] sm:w-auto" />
          <col className="w-[90px] sm:w-auto" />
        </colgroup>
        <thead className="sticky top-0 z-10 bg-muted">
          <tr>
            <th className={cn(headerCls, 'whitespace-nowrap')}>
              {t('companyHistory.colDate')}
            </th>
            <th className={headerCls}>{t('companyHistory.colType')}</th>
            <th className={cn(headerCls, 'text-right')}>
              {t('companyHistory.colChange')}
            </th>
            <th className={cn(headerCls, 'text-right')}>
              {t('companyHistory.colTotal')}
            </th>
          </tr>
        </thead>
        <tbody>
          {events.map((evt, idx) => {
            const isBlurred = blurAfter != null && idx >= blurAfter;
            return (
              <tr
                key={idx}
                className={cn(
                  'border-b border-border/40 hover:bg-muted/30 transition-colors',
                  isBlurred && 'select-none',
                )}
              >
                <td
                  className={cn(
                    cellCls,
                    'text-muted-foreground whitespace-nowrap tabular-nums',
                    isBlurred && 'blur-[5px]',
                  )}
                >
                  {evt.date}
                </td>
                <td
                  className={cn(
                    cellCls,
                    'font-medium text-foreground',
                    isBlurred && 'blur-[5px]',
                  )}
                >
                  <div className="flex items-start gap-1.5">
                    <EventIcon
                      icon={evt.icon}
                      iconBg={evt.iconBg}
                      iconColor={evt.iconColor}
                    />
                    <span className="break-words min-w-0">{evt.type}</span>
                  </div>
                </td>
                <td
                  className={cn(
                    cellCls,
                    'text-right font-semibold break-words',
                    evt.deltaColor || 'text-muted-foreground',
                    isBlurred && 'blur-[5px]',
                  )}
                >
                  {evt.delta || '—'}
                </td>
                <td
                  className={cn(
                    cellCls,
                    'text-right text-foreground font-medium break-words',
                    isBlurred && 'blur-[5px]',
                  )}
                >
                  {evt.total}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function BoardEventsTable({
  events,
  t,
  blurAfter,
  isAuthenticated = true,
}: {
  events: BoardEvent[];
  t: TFunction;
  blurAfter?: number;
  isAuthenticated?: boolean;
}) {
  if (events.length === 0) return null;
  return (
    <div className="max-h-[400px] overflow-auto">
      <table className="w-full table-fixed">
        <colgroup>
          <col className="w-[85px]" />
          <col />
          <col className="hidden sm:table-column" />
          {isAuthenticated && (
            <col className="hidden sm:table-column w-[50px]" />
          )}
          <col className="w-[140px] sm:w-auto" />
        </colgroup>
        <thead className="sticky top-0 z-10 bg-muted">
          <tr>
            <th className={cn(headerCls, 'whitespace-nowrap')}>
              {t('companyHistory.colDate')}
            </th>
            <th className={headerCls}>{t('companyHistory.colName')}</th>
            <th className={cn(headerCls, 'hidden sm:table-cell')}>
              {t('companyHistory.colRole')}
            </th>
            {isAuthenticated && (
              <th className={cn(headerCls, 'hidden sm:table-cell')}>
                {t('companyHistory.colAge')}
              </th>
            )}
            <th className={headerCls}>{t('companyHistory.colStatus')}</th>
          </tr>
        </thead>
        <tbody>
          {events.map((evt, idx) => {
            const isBlurred = blurAfter != null && idx >= blurAfter;
            return (
              <tr
                key={idx}
                className={cn(
                  'border-b border-border/40 hover:bg-muted/30 transition-colors',
                  isBlurred && 'select-none',
                )}
              >
                <td
                  className={cn(
                    cellCls,
                    'text-muted-foreground whitespace-nowrap tabular-nums',
                    isBlurred && 'blur-[5px]',
                  )}
                >
                  {evt.date}
                </td>
                <td
                  className={cn(
                    cellCls,
                    'font-medium text-foreground break-words',
                    isBlurred && 'blur-[5px]',
                  )}
                >
                  <span>{evt.name}</span>
                  {evt.role && (
                    <span className="sm:hidden text-muted-foreground text-[10px] block">
                      {evt.role}
                    </span>
                  )}
                </td>
                <td
                  className={cn(
                    cellCls,
                    'text-foreground hidden sm:table-cell',
                    isBlurred && 'blur-[5px]',
                  )}
                >
                  {evt.role || '—'}
                </td>
                {isAuthenticated && (
                  <td
                    className={cn(
                      cellCls,
                      'text-muted-foreground hidden sm:table-cell',
                      isBlurred && 'blur-[5px]',
                    )}
                  >
                    {evt.age ?? '—'}
                  </td>
                )}
                <td
                  className={cn(
                    cellCls,
                    'font-medium',
                    evt.statusColor,
                    isBlurred && 'blur-[5px]',
                  )}
                >
                  <div className="flex items-start gap-1.5">
                    <EventIcon
                      icon={evt.icon}
                      iconBg={evt.iconBg}
                      iconColor={evt.iconColor}
                    />
                    <span className="break-words min-w-0">{evt.status}</span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function InsolvencyEventsTable({
  events,
  t,
}: {
  events: InsolvencyEvent[];
  t: TFunction;
}) {
  if (events.length === 0) return null;
  return (
    <div className="max-h-[400px] overflow-y-auto overflow-x-hidden">
      <table className="w-full table-fixed">
        <colgroup>
          <col className="w-[17%] sm:w-[14%]" />
          <col className="w-[63%] sm:w-[32%]" />
          <col className="hidden sm:table-column sm:w-[22%]" />
          <col className="hidden sm:table-column sm:w-[22%]" />
          <col className="w-[20%] sm:w-[10%]" />
        </colgroup>
        <thead className="sticky top-0 z-10 bg-muted">
          <tr>
            <th className={cn(headerCls, 'whitespace-nowrap')}>
              {t('companyHistory.colDate')}
            </th>
            <th className={headerCls}>
              {t('companyHistory.colCategory', 'Kategorie')}
            </th>
            <th className={cn(headerCls, 'hidden sm:table-cell')}>
              {t('companyHistory.colAktenzeichen', 'Aktenzeichen')}
            </th>
            <th className={cn(headerCls, 'hidden sm:table-cell')}>
              {t('companyHistory.colCourt', 'Gericht / Amt')}
            </th>
            <th className={cn(headerCls, 'text-center')}>
              {t('companyHistory.colDissolved', 'Aufgelöst')}
            </th>
          </tr>
        </thead>
        <tbody>
          {events.map((evt, idx) => (
            <tr
              key={idx}
              className="border-b border-border/40 hover:bg-muted/30 transition-colors"
            >
              <td
                className={cn(
                  cellCls,
                  'text-muted-foreground whitespace-nowrap tabular-nums',
                )}
              >
                {evt.date}
              </td>
              <td className={cn(cellCls, 'font-medium text-foreground')}>
                <div className="flex items-start gap-1.5">
                  <EventIcon
                    icon={Gavel}
                    iconBg="bg-orange-100 dark:bg-orange-900/40"
                    iconColor="text-orange-600 dark:text-orange-400"
                  />
                  <span className="break-words min-w-0">
                    {t(`companyHistory.insolvencyCategory.${evt.category}`, {
                      defaultValue: evt.category,
                    })}
                  </span>
                </div>
                <div className="sm:hidden text-muted-foreground text-[10px] mt-0.5 pl-[26px]">
                  {evt.aktenzeichen} · {evt.court}
                </div>
              </td>
              <td
                className={cn(
                  cellCls,
                  'text-foreground hidden sm:table-cell tabular-nums break-words',
                )}
              >
                {evt.aktenzeichen}
              </td>
              <td
                className={cn(
                  cellCls,
                  'text-foreground hidden sm:table-cell break-words',
                )}
              >
                {evt.court}
              </td>
              <td className={cn(cellCls, 'text-center')}>
                {evt.dissolved ? (
                  <span className="inline-flex items-center rounded-full bg-red-500/10 border border-red-500/30 px-1.5 py-0.5 text-[10px] font-medium text-red-600 dark:text-red-400">
                    {t('companyHistory.dissolvedYes', 'Ja')}
                  </span>
                ) : (
                  <span className="text-muted-foreground/60">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * EU public-procurement notices this company appears in.
 *
 * Lives here rather than in a card of its own because tenders are dated
 * events like every other tab, and because the card that used to hold them
 * rendered its own header row in hardcoded English.
 *
 * `role` is per notice, not per company: the same company can be the buyer of
 * one and the winner of another, so a row states which side it was on and
 * links the counterparty.
 */
export function TendersTable({
  tenders,
  t,
  locale,
  blurAfter,
}: {
  tenders: TenderItem[];
  t: TFunction;
  locale: string;
  blurAfter?: number;
}) {
  if (tenders.length === 0) return null;
  return (
    <div className="max-h-[400px] overflow-y-auto overflow-x-hidden">
      <table className="w-full table-fixed">
        <colgroup>
          <col className="w-[17%] sm:w-[13%]" />
          <col className="w-[58%] sm:w-[35%]" />
          <col className="hidden sm:table-column sm:w-[13%]" />
          <col className="hidden sm:table-column sm:w-[21%]" />
          <col className="w-[25%] sm:w-[18%]" />
        </colgroup>
        <thead className="sticky top-0 z-10 bg-muted">
          <tr>
            <th className={cn(headerCls, 'whitespace-nowrap')}>
              {t('companyHistory.colDate')}
            </th>
            <th className={headerCls}>
              {t('companyHistory.colTenderTitle', 'Auftrag')}
            </th>
            <th className={cn(headerCls, 'hidden sm:table-cell')}>
              {t('companyHistory.colTenderRole', 'Rolle')}
            </th>
            <th className={cn(headerCls, 'hidden sm:table-cell')}>
              {t('companyHistory.colTenderCounterpart', 'Gegenseite')}
            </th>
            <th className={cn(headerCls, 'text-right')}>
              {t('companyHistory.colTenderValue', 'Wert')}
            </th>
          </tr>
        </thead>
        <tbody>
          {tenders.map((tender, idx) => {
            const isBlurred = blurAfter != null && idx >= blurAfter;
            // Awarded value where the contract is decided, estimate while it
            // is still open. Both are filtered server-side for placeholder
            // amounts, so a missing figure means "not published".
            const value = tender.awarded_value ?? tender.estimated_value;
            const currency =
              tender.awarded_currency ?? tender.value_currency ?? 'EUR';
            const counterpartSlug = tender.counterpart_name
              ? tryBuildCompanySlugFromCanonical(
                  {
                    canonical_name: tender.counterpart_canonical_name,
                    canonical_sitz: tender.counterpart_canonical_sitz,
                    canonical_register_court:
                      tender.counterpart_canonical_register_court,
                    canonical_register_type:
                      tender.counterpart_canonical_register_type,
                    canonical_register_id:
                      tender.counterpart_canonical_register_id,
                  },
                  locale as Locale,
                )
              : null;
            return (
              <tr
                key={`${tender.publication_number}-${tender.role}`}
                className="border-b border-border/40 hover:bg-muted/30 transition-colors"
              >
                <td
                  className={cn(
                    cellCls,
                    'text-muted-foreground whitespace-nowrap tabular-nums',
                    isBlurred && 'blur-[5px] select-none',
                  )}
                >
                  {formatDateISO(tender.publication_date)}
                </td>
                <td
                  className={cn(
                    cellCls,
                    'font-medium text-foreground',
                    isBlurred && 'blur-[5px] select-none',
                  )}
                >
                  <div className="flex items-start gap-1.5">
                    <EventIcon
                      icon={Gavel}
                      iconBg="bg-sky-100 dark:bg-sky-900/40"
                      iconColor="text-sky-600 dark:text-sky-400"
                    />
                    <span className="break-words min-w-0">
                      {isBlurred || !tender.publication_number ? (
                        (tender.title ?? '—')
                      ) : (
                        <a
                          href={`https://ted.europa.eu/de/notice/-/detail/${tender.publication_number}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline"
                        >
                          {tender.title ?? '—'}
                        </a>
                      )}
                    </span>
                  </div>
                  {/* The role/counterpart columns are hidden below sm, so
                      restate them here or a phone shows a date and a title
                      and no way to tell which side the company was on. */}
                  <div className="sm:hidden text-muted-foreground text-[10px] mt-0.5 pl-[26px]">
                    {t(`companyHistory.tenderRole.${tender.role}`, {
                      defaultValue: tender.role,
                    })}
                    {tender.counterpart_name ? ` · ${tender.counterpart_name}` : ''}
                  </div>
                </td>
                <td
                  className={cn(
                    cellCls,
                    'hidden sm:table-cell',
                    isBlurred && 'blur-[5px] select-none',
                  )}
                >
                  <Badge
                    variant={tender.role === 'winner' ? 'success' : 'secondary'}
                    appearance="ghost"
                    className="text-[10px]"
                  >
                    {t(`companyHistory.tenderRole.${tender.role}`, {
                      defaultValue: tender.role,
                    })}
                  </Badge>
                </td>
                <td
                  className={cn(
                    cellCls,
                    'text-foreground hidden sm:table-cell break-words',
                    isBlurred && 'blur-[5px] select-none',
                  )}
                >
                  {!tender.counterpart_name ? (
                    <span className="text-muted-foreground/60">—</span>
                  ) : counterpartSlug && !isBlurred ? (
                    <LocaleLink
                      href={counterpartSlug}
                      className="text-primary hover:underline"
                    >
                      {tender.counterpart_name}
                    </LocaleLink>
                  ) : (
                    tender.counterpart_name
                  )}
                </td>
                <td
                  className={cn(
                    cellCls,
                    'text-right tabular-nums whitespace-nowrap text-foreground',
                    isBlurred && 'blur-[5px] select-none',
                  )}
                >
                  {value != null ? (
                    formatCurrency(value, currency)
                  ) : (
                    <span className="text-muted-foreground/60">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
